/**
 * Mirror of the backends filter definition (`productdata-archive/src/configs/filters.ts`), so the
 * search form knows which query params are property filters and which values the backend accepts.
 *
 * The dropdown values themselves are not listed here, they are requested from `/configs/filters`.
 */

/** Filters which are applied by selecting one of their distinct values, in display order */
export const dropdownFilterKeys = [
  'program',
  /** Gattung */
  'genre',
  'family',
  'control',
  'illuminationDistribution',
  'housingColor',
  'size',
  'colorCombination',
] as const;

export type DropdownFilterKey = typeof dropdownFilterKeys[number];

/** Only whole numbers, i.e. a color temperature in kelvin or a luminous flux in lumens */
const integerPattern = /^\d+$/;

/** A decimal with an english or a german separator, i.e. "6.1" or "6,1" */
const decimalPattern = /^\d+([.,]\d+)?$/;

/**
 * Characters a user may type into a whole number field. Unlike the patterns above it matches an
 * unfinished value as well, as it is checked on every keystroke.
 */
const integerInput = /^\d*$/;

/** Characters a user may type into a decimal field, i.e. "6", "6," and "6,1" while typing "6,1" */
const decimalInput = /^\d*([.,]\d*)?$/;

/**
 * Filters which are applied by a freely entered number. `inputPattern` keeps a field free of
 * anything but a number while typing, `isValid` additionally enforces the bounds of the backend,
 * as the search is executed before a user finished entering a value.
 */
export const numericFilters = {
  /** Color temperature in kelvin */
  colorTemperature: {
    unit: 'K',
    inputPattern: integerInput,
    inputMode: 'numeric',
    isValid: (value: string): boolean =>
      integerPattern.test(value) && Number(value) >= 1000 && Number(value) <= 10000,
  },
  /** Power input in watts, may use a german decimal separator */
  power: {
    unit: 'W',
    inputPattern: decimalInput,
    inputMode: 'decimal',
    isValid: (value: string): boolean =>
      decimalPattern.test(value) && parseFloat(value.replace(',', '.')) > 0,
  },
  /** Luminous flux in lumens */
  lumens: {
    unit: 'lm',
    inputPattern: integerInput,
    inputMode: 'numeric',
    isValid: (value: string): boolean => integerPattern.test(value),
  },
} as const;

export type NumericFilterKey = keyof typeof numericFilters;

export const numericFilterKeys = Object.keys(numericFilters) as NumericFilterKey[];

/** All params which satisfy the backends "a search needs a term or at least one filter" rule */
export const filterKeys: string[] = [...dropdownFilterKeys, ...numericFilterKeys];

/**
 * Determines if a filter is applied by a freely entered number and therefore needs to be validated
 * before it is sent to the backend
 * @param {string} key - query param to check
 */
export const isNumericFilter = (key: string): key is NumericFilterKey => key in numericFilters;
